import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { staggerContainer, fadeInUp } from '../animations';
import { gulfCountries } from '../data';

/**
 * GulfSaudiSection — primary keyword cluster (Gulf & Saudi recruitment).
 * Eager-loaded (sits in the upper-fold for SEO).
 */
const GulfSaudiSection = () => (
  <section
    className="py-20 md:py-28 bg-slate-950 relative overflow-hidden"
    aria-labelledby="gulf-saudi-heading"
    data-testid="gulf-saudi-section"
  >
    <div className="absolute inset-0 opacity-[0.04] pointer-events-none">
      <div className="absolute top-10 right-10 w-96 h-96 bg-[#C5A028] rounded-full blur-[160px]" />
      <div className="absolute bottom-10 left-10 w-96 h-96 bg-green-700 rounded-full blur-[160px]" />
    </div>

    <div className="container max-w-7xl mx-auto px-6 md:px-12 relative z-10">
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: '-100px' }}
        variants={staggerContainer}
        className="text-center mb-14"
      >
        <motion.p variants={fadeInUp} className="text-[#C5A028] text-sm font-semibold uppercase tracking-widest mb-4">
          Our Core Specialisation
        </motion.p>
        <motion.h2
          id="gulf-saudi-heading"
          variants={fadeInUp}
          className="font-syne text-4xl md:text-5xl font-bold text-white mb-5 leading-tight"
        >
          Gulf &amp; Saudi Arabia Recruitment
        </motion.h2>
        <motion.p variants={fadeInUp} className="text-slate-400 max-w-2xl mx-auto leading-relaxed">
          We are India's <span className="text-white font-medium">Royal Saudi Embassy-empanelled</span> recruitment partner
          for Gulf employers — moving 1,200+ workers to Saudi, UAE, Qatar, Kuwait, Oman and Bahrain every year
          across construction, oil &amp; gas, healthcare and hospitality.
        </motion.p>
      </motion.div>

      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: '-60px' }}
        variants={staggerContainer}
        className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5 mb-12"
      >
        {gulfCountries.map((c) => (
          <motion.div key={c.name} variants={fadeInUp}>
            <Link
              to={c.href}
              className={`group block rounded-2xl p-5 text-center border transition-all duration-300 h-full ${
                c.primary
                  ? 'bg-gradient-to-br from-[#C5A028]/15 to-slate-900 border-[#C5A028]/40 hover:border-[#C5A028]'
                  : 'bg-slate-900/50 border-white/5 hover:border-[#C5A028]/30 hover:bg-slate-900'
              }`}
              data-testid={`gulf-country-${c.name.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <div className="text-4xl mb-2 transition-transform group-hover:scale-110">{c.flag}</div>
              <div className="font-syne font-bold text-white text-base">{c.name}</div>
              <div className="text-[11px] text-slate-400 mt-1 uppercase tracking-wider">{c.tag}</div>
              <div className="mt-3 text-xs text-[#C5A028] font-semibold flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                Explore <ArrowRight className="w-3 h-3" />
              </div>
            </Link>
          </motion.div>
        ))}
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="bg-gradient-to-r from-[#C5A028]/10 via-slate-900/60 to-[#C5A028]/10 border border-[#C5A028]/20 rounded-2xl p-6 md:p-8 flex flex-col md:flex-row items-center justify-between gap-5"
      >
        <div className="text-center md:text-left">
          <p className="text-xs uppercase tracking-widest text-[#C5A028] font-semibold mb-1">Hiring Bulk Workforce?</p>
          <p className="text-white font-syne text-xl md:text-2xl leading-snug">
            We deploy <span className="text-[#C5A028]">100-1,000+ workers</span> per project across the Gulf.
          </p>
        </div>
        <Link
          to="/contact?service=bulk-recruitment"
          className="inline-flex items-center gap-2 bg-[#C5A028] hover:bg-[#EAB308] text-black font-bold px-6 py-3 rounded-full transition-all whitespace-nowrap"
          data-testid="bulk-recruitment-cta"
        >
          Request Manpower <ArrowRight className="w-4 h-4" />
        </Link>
      </motion.div>
    </div>
  </section>
);

export default GulfSaudiSection;
